import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api, uploadUrl } from '../hooks/useApi';
import Icon from '../components/Icon';
import './CatalogueProduit.css';

const ALLERGENES_LISTE = ['gluten', 'soya', 'laitier', 'oeufs', 'noix', 'poisson'];
const CATEGORIES = ['viande', 'sauce', 'side', 'boisson', 'extra', 'forfait'];
const SOUS_CATEGORIES = {
  viande: ['porc', 'boeuf', 'volaille', 'poisson', 'vegetarien'],
  sauce: [], side: [], boisson: [], extra: [], forfait: [],
};
const WC_STATUTS = [
  { value: 'draft',   label: 'Brouillon' },
  { value: 'publish', label: 'Publié' },
  { value: 'private', label: 'Privé' },
];

const FORM_INIT = {
  nom: '', nom_court: '', slug: '', categorie: 'viande', sous_categorie: '',
  emoji: '', description_courte: '', description_longue: '',
  prix_base: '', unite_prix: 'portion',
  allergenes: [], sans_gluten: false, sans_msg: true, sans_nitrite: true, naturel_100pct: true,
  origine: '', fournisseur_type: '',
  wc_sku: '', wc_categorie: '', wc_statut: 'draft', wc_description: '', wc_tags: '',
  actif: true, saisonnier: false, saison_debut: '', saison_fin: '',
  disponible: true, notes_internes: '', ordre_affichage: 0,
};

function jsonToAllergenes(jsonStr) {
  try { return JSON.parse(jsonStr || '[]'); } catch { return []; }
}

function formaterDateFR(isoStr) {
  if (!isoStr) return null;
  const d = new Date(isoStr);
  if (isNaN(d.getTime())) return isoStr;
  return d.toLocaleDateString('fr-CA', { year: 'numeric', month: 'long', day: 'numeric' });
}

/* En-tête de section de formulaire */
function SectionHeader({ icon, titre }) {
  return (
    <div className="card-header">
      <div className="card-header-left">
        <span className="card-header-icon"><Icon name={icon} size={15} /></span>
        <h3 className="card-title">{titre}</h3>
      </div>
    </div>
  );
}

export default function CatalogueProduit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === 'new';
  const photoRef = useRef();

  const [form,        setForm]        = useState(FORM_INIT);
  const [loading,     setLoading]     = useState(!isNew);
  const [saving,      setSaving]      = useState(false);
  const [pushing,     setPushing]     = useState(false);
  const [msg,         setMsg]         = useState('');
  const [msgErr,      setMsgErr]      = useState(false);
  const [erreur,      setErreur]      = useState('');
  const [photoPreview,setPhotoPreview]= useState('');
  const [infoStock,   setInfoStock]   = useState(null);

  useEffect(() => {
    if (isNew) return;
    api.get(`/catalogue/${id}`).then(data => {
      setForm({
        nom:               data.nom || '',
        nom_court:         data.nom_court || '',
        slug:              data.slug || '',
        categorie:         data.categorie || 'viande',
        sous_categorie:    data.sous_categorie || '',
        emoji:             data.emoji || '',
        description_courte: data.description_courte || '',
        description_longue: data.description_longue || '',
        prix_base:         data.prix_base ?? '',
        unite_prix:        data.unite_prix || 'portion',
        allergenes:        jsonToAllergenes(data.allergenes),
        sans_gluten:       !!data.sans_gluten,
        sans_msg:          !!data.sans_msg,
        sans_nitrite:      !!data.sans_nitrite,
        naturel_100pct:    !!data.naturel_100pct,
        origine:           data.origine || '',
        fournisseur_type:  data.fournisseur_type || '',
        wc_sku:            data.wc_sku || '',
        wc_categorie:      data.wc_categorie || '',
        wc_statut:         data.wc_statut || 'draft',
        wc_description:    data.wc_description || '',
        wc_tags:           (jsonToAllergenes(data.wc_tags || '[]')).join(', '),
        actif:             !!data.actif,
        saisonnier:        !!data.saisonnier,
        saison_debut:      data.saison_debut || '',
        saison_fin:        data.saison_fin || '',
        disponible:        !!data.disponible,
        notes_internes:    data.notes_internes || '',
        ordre_affichage:   data.ordre_affichage || 0,
        wc_product_id:     data.wc_product_id,
        wc_derniere_sync:  data.wc_derniere_sync,
        photo_principale_url: data.photo_principale_url,
      });
      if (data.photo_principale_url) {
        const url = data.photo_principale_url.startsWith('http')
          ? data.photo_principale_url
          : uploadUrl(data.photo_principale_url);
        setPhotoPreview(url);
      }
      setInfoStock({
        stock_reel:         data.stock_reel ?? 0,
        nb_batches:         data.nb_batches ?? 0,
        dernier_batch_date: data.dernier_batch_date ?? null,
      });
      setLoading(false);
    }).catch(e => { setErreur(e.message); setLoading(false); });
  }, [id, isNew]);

  function setF(key, val) { setForm(f => ({ ...f, [key]: val })); }

  function showMsg(m, isErr = false) {
    setMsg(m);
    setMsgErr(isErr);
    setTimeout(() => setMsg(''), 4000);
  }

  function toggleAllergene(a) {
    setForm(f => {
      const arr = f.allergenes.includes(a)
        ? f.allergenes.filter(x => x !== a)
        : [...f.allergenes, a];
      return { ...f, allergenes: arr };
    });
  }

  async function handlePhotoChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    if (isNew) {
      showMsg('Sauvegardez le produit d\'abord avant d\'uploader une photo.', true);
      return;
    }
    const formData = new FormData();
    formData.append('photo', file);
    try {
      const data = await api.postForm(`/catalogue/${id}/photo`, formData);
      const url = data.photo_principale_url.startsWith('http')
        ? data.photo_principale_url
        : uploadUrl(data.photo_principale_url);
      setPhotoPreview(url);
      setF('photo_principale_url', data.photo_principale_url);
      showMsg('Photo mise à jour');
    } catch (err) {
      showMsg('Erreur upload : ' + (err.response?.data?.error || err.message), true);
    }
  }

  async function sauvegarder(e) {
    e.preventDefault();
    try {
      setSaving(true);
      const payload = {
        ...form,
        allergenes:    JSON.stringify(form.allergenes),
        wc_tags:       JSON.stringify(form.wc_tags.split(',').map(t => t.trim()).filter(Boolean)),
        prix_base:     Number(form.prix_base) || 0,
        sans_gluten:   form.sans_gluten   ? 1 : 0,
        sans_msg:      form.sans_msg      ? 1 : 0,
        sans_nitrite:  form.sans_nitrite  ? 1 : 0,
        naturel_100pct:form.naturel_100pct? 1 : 0,
        actif:         form.actif         ? 1 : 0,
        saisonnier:    form.saisonnier    ? 1 : 0,
        disponible:    form.disponible    ? 1 : 0,
      };
      if (isNew) {
        const data = await api.post('/catalogue', payload);
        showMsg('Produit créé');
        navigate(`/catalogue/${data.id}`);
      } else {
        await api.put(`/catalogue/${id}`, payload);
        showMsg('Produit sauvegardé');
      }
    } catch (err) {
      showMsg('Erreur : ' + err.message, true);
    } finally {
      setSaving(false);
    }
  }

  async function pusherWC() {
    if (!form.wc_sku) { showMsg('SKU WooCommerce requis pour pousser.', true); return; }
    try {
      setPushing(true);
      const data = await api.post(`/catalogue/${id}/push-wc`);
      showMsg(`Produit poussé vers WooCommerce (ID WC: ${data.wc_product_id})`);
      setF('wc_product_id', data.wc_product_id);
    } catch (err) {
      showMsg('Erreur WC : ' + err.message, true);
    } finally {
      setPushing(false);
    }
  }

  /* ── États de chargement / erreur ── */
  if (loading) {
    return (
      <div className="cp-page">
        <div className="cp-skel-header">
          <div className="skeleton" style={{ height: 32, width: 120, borderRadius: 'var(--radius-md)' }} />
          <div className="skeleton" style={{ height: 38, width: 260, borderRadius: 4 }} />
        </div>
        {[...Array(4)].map((_, i) => (
          <div key={i} className="card" style={{ marginBottom: 'var(--space-4)', padding: 'var(--space-6)' }}>
            <div className="skeleton" style={{ height: 16, width: 160, borderRadius: 4, marginBottom: 'var(--space-5)' }} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div className="skeleton" style={{ height: 38, borderRadius: 'var(--radius-md)' }} />
              <div className="skeleton" style={{ height: 38, borderRadius: 'var(--radius-md)' }} />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (erreur && !form.nom) {
    return (
      <div className="cp-page">
        <div className="error-inline">
          <Icon name="erreur" size={16} />
          {erreur}
          <button className="btn btn-ghost btn--sm" style={{ marginLeft: 'auto' }}
            onClick={() => window.location.reload()}>Réessayer</button>
        </div>
      </div>
    );
  }

  return (
    <div className="cp-page">

      {/* ── En-tête de page ── */}
      <div className="page-header" style={{ marginBottom: 'var(--space-5)' }}>
        <div className="page-header-text">
          <button className="btn btn-ghost btn--sm cp-retour" onClick={() => navigate('/catalogue')}>
            <Icon name="retour" size={14} />
            Catalogue
          </button>
          <h1 className="page-title">{isNew ? 'Nouveau produit' : form.nom}</h1>
        </div>
      </div>

      {/* ── Flash ── */}
      {msg && (
        <div className={`cp-flash rise-in ${msgErr ? 'cp-flash--err' : 'cp-flash--ok'}`}>
          <Icon name={msgErr ? 'erreur' : 'succes'} size={15} />
          {msg}
        </div>
      )}

      <form onSubmit={sauvegarder} className="cp-form">

        {/* ── Stock réel (lecture seule) ── */}
        {!isNew && infoStock !== null && form.wc_sku && (
          <div className="card cp-section cp-stock-section rise-in">
            <SectionHeader icon="analytics" titre="Stock réel (production)" />
            <div className="cp-stock-row">
              <div className="cp-kpi">
                <span className="cp-kpi-label">Stock disponible</span>
                {infoStock.stock_reel > 0
                  ? <span className="cp-kpi-value" style={{ color: 'var(--accent)' }}>
                      {infoStock.stock_reel} unité{infoStock.stock_reel > 1 ? 's' : ''}
                    </span>
                  : <span className="cp-kpi-value" style={{ color: 'var(--danger)' }}>Rupture de stock</span>
                }
              </div>
              <div className="cp-kpi">
                <span className="cp-kpi-label">Batches produits</span>
                <span className="cp-kpi-value">{infoStock.nb_batches}</span>
              </div>
              {infoStock.dernier_batch_date && (
                <div className="cp-kpi">
                  <span className="cp-kpi-label">Dernier batch</span>
                  <span className="cp-kpi-value">{formaterDateFR(infoStock.dernier_batch_date)}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Photo principale ── */}
        <div className="card cp-section rise-in">
          <SectionHeader icon="photo" titre="Photo principale" />
          <div className="cp-photo-row">
            {photoPreview ? (
              <img src={photoPreview} alt="Aperçu" className="cp-photo-preview" />
            ) : (
              <div className="cp-photo-placeholder">
                <Icon name="photo" size={32} />
              </div>
            )}
            <div>
              <input
                type="file" ref={photoRef} accept="image/*"
                style={{ display: 'none' }} onChange={handlePhotoChange}
              />
              <button type="button" className="btn btn-ghost btn--sm" onClick={() => photoRef.current.click()}>
                <Icon name="televerser" size={14} />
                {photoPreview ? 'Changer la photo' : 'Uploader une photo'}
              </button>
              {isNew && (
                <p className="muted" style={{ fontSize: 11, marginTop: 6 }}>
                  Créez d'abord le produit, puis uploadez la photo.
                </p>
              )}
            </div>
          </div>
        </div>

        {/* ── Infos générales ── */}
        <div className="card cp-section rise-in">
          <SectionHeader icon="package" titre="Infos générales" />
          <div className="cp-grid-2">
            <div className="field">
              <label>Nom *</label>
              <input type="text" value={form.nom} onChange={e => setF('nom', e.target.value)} required />
            </div>
            <div className="field">
              <label>Nom court</label>
              <input type="text" value={form.nom_court} onChange={e => setF('nom_court', e.target.value)} />
            </div>
          </div>
          <div className="cp-grid-2">
            <div className="field">
              <label>Catégorie *</label>
              <select value={form.categorie} onChange={e => setF('categorie', e.target.value)} required>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="field">
              <label>Sous-catégorie</label>
              <select value={form.sous_categorie} onChange={e => setF('sous_categorie', e.target.value)}>
                <option value="">—</option>
                {(SOUS_CATEGORIES[form.categorie] || []).map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div className="cp-grid-3">
            <div className="field">
              <label>Prix de base ($)</label>
              <input type="number" min="0" step="0.01" value={form.prix_base} onChange={e => setF('prix_base', e.target.value)} />
            </div>
            <div className="field">
              <label>Unité prix</label>
              <input type="text" value={form.unite_prix} onChange={e => setF('unite_prix', e.target.value)} placeholder="portion, kg, unité…" />
            </div>
            <div className="field">
              <label>Ordre d'affichage</label>
              <input type="number" value={form.ordre_affichage} onChange={e => setF('ordre_affichage', e.target.value)} />
            </div>
          </div>
          <div className="cp-grid-2">
            <div className="field">
              <label>Slug URL</label>
              <input type="text" value={form.slug} onChange={e => setF('slug', e.target.value)} placeholder="porc-effiloche" />
            </div>
            <div className="field">
              <label>Emoji (optionnel)</label>
              <input type="text" value={form.emoji} onChange={e => setF('emoji', e.target.value)} maxLength={2} style={{ textAlign: 'center', fontSize: 20 }} />
            </div>
          </div>
          <div className="cp-checkboxes">
            <label className="cp-checkbox-label">
              <input type="checkbox" checked={form.actif} onChange={e => setF('actif', e.target.checked)} />
              Actif
            </label>
            <label className="cp-checkbox-label">
              <input type="checkbox" checked={form.disponible} onChange={e => setF('disponible', e.target.checked)} />
              Disponible
            </label>
            <label className="cp-checkbox-label">
              <input type="checkbox" checked={form.saisonnier} onChange={e => setF('saisonnier', e.target.checked)} />
              Saisonnier
            </label>
          </div>
          {form.saisonnier && (
            <div className="cp-grid-2">
              <div className="field">
                <label>Début saison (MM-DD)</label>
                <input type="text" value={form.saison_debut} onChange={e => setF('saison_debut', e.target.value)} placeholder="05-01" />
              </div>
              <div className="field">
                <label>Fin saison (MM-DD)</label>
                <input type="text" value={form.saison_fin} onChange={e => setF('saison_fin', e.target.value)} placeholder="10-31" />
              </div>
            </div>
          )}
        </div>

        {/* ── Descriptions ── */}
        <div className="card cp-section rise-in">
          <SectionHeader icon="document" titre="Descriptions" />
          <div className="field" style={{ marginBottom: 'var(--space-4)' }}>
            <label>Description courte (1-2 lignes, visible dans les cards)</label>
            <textarea rows={2} value={form.description_courte} onChange={e => setF('description_courte', e.target.value)} />
          </div>
          <div className="field">
            <label>Description longue (menu PDF + pages client)</label>
            <textarea rows={5} value={form.description_longue} onChange={e => setF('description_longue', e.target.value)} />
          </div>
        </div>

        {/* ── Allergènes & Certifications ── */}
        <div className="card cp-section rise-in">
          <SectionHeader icon="attention" titre="Allergènes & Certifications" />
          <div className="field" style={{ marginBottom: 'var(--space-4)' }}>
            <label>Allergènes</label>
            <div className="cp-checkboxes">
              {ALLERGENES_LISTE.map(a => (
                <label key={a} className="cp-checkbox-label">
                  <input type="checkbox" checked={form.allergenes.includes(a)} onChange={() => toggleAllergene(a)} />
                  {a.charAt(0).toUpperCase() + a.slice(1)}
                </label>
              ))}
            </div>
          </div>
          <div className="field" style={{ marginBottom: 'var(--space-4)' }}>
            <label>Certifications</label>
            <div className="cp-checkboxes">
              {[['sans_gluten','Sans gluten'],['sans_msg','Sans MSG'],['sans_nitrite','Sans nitrite'],['naturel_100pct','100% naturel']].map(([k, l]) => (
                <label key={k} className="cp-checkbox-label">
                  <input type="checkbox" checked={form[k]} onChange={e => setF(k, e.target.checked)} />
                  {l}
                </label>
              ))}
            </div>
          </div>
          <div className="cp-grid-2">
            <div className="field">
              <label>Origine / storytelling</label>
              <input type="text" value={form.origine} onChange={e => setF('origine', e.target.value)} placeholder="Fumé au bois d'érable du Québec" />
            </div>
            <div className="field">
              <label>Type fournisseur</label>
              <input type="text" value={form.fournisseur_type} onChange={e => setF('fournisseur_type', e.target.value)} placeholder="Wagyu canadien | Local QC" />
            </div>
          </div>
        </div>

        {/* ── WooCommerce ── */}
        <div className="card cp-section rise-in">
          <SectionHeader icon="boutique" titre="WooCommerce" />
          <div className="cp-grid-2">
            <div className="field">
              <label>SKU WooCommerce</label>
              <input type="text" value={form.wc_sku} onChange={e => setF('wc_sku', e.target.value)} placeholder="PE400" />
            </div>
            <div className="field">
              <label>Catégorie WC</label>
              <input type="text" value={form.wc_categorie} onChange={e => setF('wc_categorie', e.target.value)} placeholder="Formats Fixes" />
            </div>
          </div>
          <div className="cp-grid-2">
            <div className="field">
              <label>Statut WooCommerce</label>
              <select value={form.wc_statut} onChange={e => setF('wc_statut', e.target.value)}>
                {WC_STATUTS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <div className="field">
              <label>Tags WC (séparés par virgule)</label>
              <input type="text" value={form.wc_tags} onChange={e => setF('wc_tags', e.target.value)} placeholder="bbq, fumé, porc, québec" />
            </div>
          </div>
          <div className="field" style={{ marginBottom: 'var(--space-4)' }}>
            <label>Description WooCommerce (optimisée pour le site web)</label>
            <textarea rows={4} value={form.wc_description} onChange={e => setF('wc_description', e.target.value)} />
          </div>
          {form.wc_product_id && (
            <div className="cp-wc-info">
              <Icon name="sku" size={14} />
              ID WC : <strong>{form.wc_product_id}</strong>
              {form.wc_derniere_sync && (
                <span className="muted"> · Dernière sync : {form.wc_derniere_sync}</span>
              )}
            </div>
          )}
          {!isNew && (
            <button type="button" className="btn btn-ghost" onClick={pusherWC} disabled={pushing}>
              <Icon name="televerser" size={15} />
              {pushing ? 'Envoi…' : 'Pousser vers WooCommerce'}
            </button>
          )}
        </div>

        {/* ── Notes internes ── */}
        <div className="card cp-section rise-in">
          <SectionHeader icon="liste" titre="Notes internes" />
          <div className="field">
            <textarea rows={3} value={form.notes_internes} onChange={e => setF('notes_internes', e.target.value)} />
          </div>
        </div>

        {/* ── Actions ── */}
        <div className="cp-submit-row">
          <button type="button" className="btn btn-ghost" onClick={() => navigate('/catalogue')}>Annuler</button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            <Icon name={saving ? 'timer' : 'sauvegarder'} size={15} />
            {saving ? 'Sauvegarde…' : (isNew ? 'Créer le produit' : 'Sauvegarder les modifications')}
          </button>
        </div>

      </form>
    </div>
  );
}
